import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { AuthService } from "./AuthService.ts";
import { FirebaseAuthAdapter } from "./FirebaseAuthAdapter.ts";
import { FirebaseApp } from "firebase/app";
import { ConfigType } from "../../config/ConfigType.ts";
import { ProductEventTarget } from "../product-events/ProductEventTarget.ts";

export const useAuthService_internal = (
  firebaseApp: FirebaseApp,
  config: Pick<ConfigType, "microsoft">,
  localStorage: Pick<Storage, "setItem" | "getItem" | "removeItem">,
  enableFirebaseEmulator: boolean,
  productEvents: ProductEventTarget,
) => {
  const [authService] = useState(
    () =>
      new AuthService(
        firebaseApp,
        config,
        localStorage,
        new FirebaseAuthAdapter(),
        productEvents,
      ),
  );
  const [isReady, setIsReady] = useState(false);
  const previousFirebaseEmulator = useRef<boolean | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (
      previousFirebaseEmulator.current !== null &&
      previousFirebaseEmulator.current !== enableFirebaseEmulator
    ) {
      const action = enableFirebaseEmulator ? "enabled" : "disabled";
      const refresh = confirm(
        `You have ${action} the Firebase Emulator. Do you want to refresh the page to apply the changes?`,
      );
      if (refresh) {
        window.location.reload();
        return;
      }
    }
    previousFirebaseEmulator.current = enableFirebaseEmulator;
  }, [enableFirebaseEmulator]);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        if (enableFirebaseEmulator) {
          console.log("Enabling Firebase Emulator");
          authService.enableEmulator();
        }

        await authService.initialize();

        const result = await authService.completeMagicLinkLogin(
          document.location.href,
        );
        if (result.shouldRedirect) {
          navigate("/");
        }

        setIsReady(true);
      } catch (error) {
        console.error("Failed to initialize AuthService:", error);
        setIsReady(true);
      }
    };

    initializeAuth();
  }, [authService, enableFirebaseEmulator, navigate]);

  return { authService, isReady };
};
